/**
 * External dependencies
 */
import type { ReactNode, ChangeEvent } from 'react';

/**
 * WordPress dependencies
 */
import { _x } from '@safe-wordpress/i18n';
import {
	FormFileUpload,
	NavigableMenu,
	MenuItem,
	Dropdown,
	Button,
	type DropdownProps,
} from '@safe-wordpress/components';
import { useDispatch } from '@safe-wordpress/data';
import { upload, media as mediaIcon } from '@safe-wordpress/icons';
import { stripHTML } from '@safe-wordpress/dom';
import { store as noticesStore } from '@safe-wordpress/notices';
import {
	uploadMedia,
	MediaUpload,
	Attachment,
} from '@safe-wordpress/media-utils';

type UploadMediaOptions = Parameters< typeof uploadMedia >[ 0 ];

const noop = () => {};
let uniqueId = 0;

type MediaReplaceFlowProps = {
	readonly mediaURL: string;
	readonly mediaId: number;
	readonly allowedTypes: UploadMediaOptions[ 'allowedTypes' ];
	readonly accept: string | undefined;
	readonly onError: ( message: string ) => void;
	readonly onSelect: ( media: Attachment ) => void;
	readonly onReset: () => void;
	readonly onFilesUpload?: (
		files: UploadMediaOptions[ 'filesList' ]
	) => void;
	readonly name: ReactNode;
	readonly children: ReactNode;
	readonly handleUpload?: boolean;
	readonly popoverProps: DropdownProps[ 'popoverProps' ];
	readonly renderToggle: DropdownProps[ 'renderToggle' ];
};

export const MediaReplaceFlow = ( {
	mediaURL,
	mediaId,
	allowedTypes,
	accept,
	onError,
	onSelect,
	onReset,
	onFilesUpload = noop,
	name = _x( 'Replace', 'command', 'nelio-ab-testing' ),
	children,
	handleUpload = true,
	popoverProps,
	renderToggle,
}: MediaReplaceFlowProps ): JSX.Element => {
	const errorNoticeID = `block-editor/media-replace-flow/error-notice/${ ++uniqueId }`;

	const { createNotice, removeNotice } = useDispatch( noticesStore );

	const onUploadError = ( message: Error | string ) => {
		const safeMessage = stripHTML(
			'string' === typeof message ? message : message.toString()
		);
		if ( onError ) {
			onError( safeMessage );
			return;
		}
		// We need to set a timeout for showing the notice
		// so that VoiceOver and possibly other screen readers
		// can announce the error after the toolbar button
		// regains focus once the upload dialog closes.
		// Otherwise VO simply skips over the notice and announces
		// the focused element and the open menu.
		setTimeout( () => {
			void createNotice( 'error', safeMessage, {
				speak: true,
				id: errorNoticeID,
				isDismissible: true,
			} );
		}, 1000 );
	};

	const selectMedia = ( media: Attachment, closeMenu: () => void ) => {
		closeMenu();
		// Calling `onSelect` after the state update since it might unmount the component.
		onSelect( media );
		void removeNotice( errorNoticeID );
	};

	const uploadFiles = (
		event: ChangeEvent< HTMLInputElement >,
		closeMenu: () => void
	) => {
		const files = ( event.target.files ?? [] ) as unknown as File[];
		if ( ! handleUpload ) {
			closeMenu();
			return onSelect( files as unknown as Attachment );
		}
		onFilesUpload( files );
		uploadMedia( {
			allowedTypes,
			filesList: files,
			onFileChange: ( [ media ] ) => {
				selectMedia( media as Attachment, closeMenu );
			},
			onError: onUploadError,
		} );
	};

	return (
		<Dropdown
			popoverProps={ popoverProps }
			contentClassName="block-editor-media-replace-flow__options"
			renderToggle={ ( { isOpen, onToggle } ) => {
				if ( renderToggle ) {
					return renderToggle( {
						'aria-expanded': isOpen,
						'aria-haspopup': 'true',
						onClick: onToggle,
						children: name,
					} as unknown as {
						isOpen: boolean;
						onToggle: () => void;
						onClose: () => void;
					} );
				}
				return (
					<Button
						aria-expanded={ isOpen }
						aria-haspopup="true"
						onClick={ onToggle }
					>
						{ name }
					</Button>
				);
			} }
			renderContent={ ( { onClose } ) => (
				<>
					<NavigableMenu className="block-editor-media-replace-flow__media-upload-menu">
						<MediaUpload
							value={ mediaId }
							onSelect={ ( media: Attachment ) =>
								selectMedia( media, onClose )
							}
							allowedTypes={ allowedTypes }
							render={ ( { open }: { open: () => void } ) => (
								<MenuItem
									role="menuitem"
									icon={ mediaIcon }
									onClick={ open }
								>
									{ _x(
										'Open Media Library',
										'command',
										'nelio-ab-testing'
									) }
								</MenuItem>
							) }
						/>
						<FormFileUpload
							onChange={ ( event ) => {
								uploadFiles( event, onClose );
							} }
							accept={ accept }
							render={ ( { openFileDialog } ) => {
								return (
									<MenuItem
										role="menuitem"
										icon={ upload }
										onClick={ () => {
											openFileDialog();
										} }
									>
										{ _x(
											'Upload',
											'verb',
											'nelio-ab-testing'
										) }
									</MenuItem>
								);
							} }
						/>
						{ mediaURL && onReset && (
							<MenuItem
								role="menuitem"
								onClick={ () => {
									onReset();
									onClose();
								} }
							>
								{ _x( 'Reset', 'command', 'nelio-ab-testing' ) }
							</MenuItem>
						) }
						{ children }
					</NavigableMenu>
				</>
			) }
		/>
	);
};
